/* Board-side particle effects — per-cell confetti pops on first-green
   plus the big celebration burst that runs concurrent with the
   masthead fire-breath on win.

   Renders nothing visible itself (confetti uses a full-viewport canvas
   the library manages internally). Mounts once at the top of the tree
   and reacts to game-state signals. */

import { effect } from "@preact/signals";
import { useEffect } from "preact/hooks";
import * as gameState from "../game/state";
import {
  HINT_COST,
  analysis,
  celebrationTrigger,
  guesses,
  hintPurchasedAt,
  newGreensInLastGuess,
  status,
} from "../game/state";
import { fireWinCelebration, popCell } from "./cellConfetti";
import {
  REVEAL_FLIP_MS,
  buildRevealSlots,
  changedLetterIndices,
  prefersReducedMotion,
  revealDelayFor,
} from "./reveal";

/** Resolve the DOM cell at (slot, letterIndex). Rows interleave separator
    spans with cells, so we filter to .cell within the chosen row. */
function findCell(slot: number, letterIndex: number): Element | null {
  const row = document.querySelectorAll(".board > .row")[slot];
  if (!row) return null;
  return row.querySelectorAll(".cell")[letterIndex] ?? null;
}

export function BoardEffects() {
  useEffect(() => {
    // Per-cell pops: every time the "newly-green" computed re-emits
    // with a non-empty list, fire one confetti burst per cell at the
    // moment that cell finishes its flip-reveal — pair index → delay,
    // plus the flip duration so the pop coincides with the colour
    // settling, not with the edge-on midpoint.
    let pendingTimers: number[] = [];
    const cancelPending = () => {
      for (const t of pendingTimers) window.clearTimeout(t);
      pendingTimers = [];
    };
    // First effect run is the on-mount snapshot of current state, not a
    // user submission. If the page loaded with a hydrated finished
    // playthrough, every winning position will appear in `indices` —
    // popping them would replay the celebration on a returning visit.
    // Mid-session hydration (Play Again to a previously-won puzzle) is
    // covered separately by the `gameState.isHydrating` flag.
    let greensFirstRun = true;
    const stopGreens = effect(() => {
      const indices = newGreensInLastGuess.value;
      // Cancel any still-pending pops from a previous submission so they
      // can't fire against the wrong slot if the player advances or
      // resets before the flip-reveal finishes.
      cancelPending();
      const wasFirstRun = greensFirstRun;
      greensFirstRun = false;
      if (indices.length === 0) return;
      if (wasFirstRun) return;
      // Hydration installs a finished playthrough's guesses in one
      // batched write — `newGreensInLastGuess` lights up with every
      // winning position, but no real submission happened. Skip pops.
      if (gameState.isHydrating) return;
      // Visual slot of the just-submitted guess (mirrors Board.tsx's
      // slotForGuess: pre-hint guesses keep their slot; post-hint
      // guesses are pushed down by HINT_COST).
      const guessIdx = guesses.value.length - 1;
      if (guessIdx < 0) return;
      const purchased = hintPurchasedAt.value;
      const slot =
        purchased != null && guessIdx >= purchased
          ? guessIdx + HINT_COST
          : guessIdx;
      const letterCount = analysis.value.letterCount;
      const reduced = prefersReducedMotion();
      // Re-derive the same slot map Row uses so confetti fires when the
      // matching cell finishes its flip. Pairs unchanged since the
      // previous guess have no slot — those pops fire at t=0 (the cell
      // never animated). In practice "new green" implies "changed", so
      // this branch is mostly defensive.
      const allGuesses = guesses.value;
      const previous =
        guessIdx > 0 ? allGuesses[guessIdx - 1]! : null;
      const slots = reduced
        ? new Map<number, number>()
        : buildRevealSlots(
            changedLetterIndices(allGuesses[guessIdx]!, previous),
            letterCount,
          );
      // Defer one frame so the just-submitted row has rendered.
      requestAnimationFrame(() => {
        for (const i of indices) {
          const delay = reduced
            ? 0
            : revealDelayFor(i, letterCount, slots);
          const fireAt = delay == null ? 0 : delay + REVEAL_FLIP_MS;
          const t = window.setTimeout(() => {
            const cell = findCell(slot, i);
            if (cell) popCell(cell);
          }, fireAt);
          pendingTimers.push(t);
        }
      });
    });

    // Win burst: fires on every playing → won transition AND on every
    // celebrationTrigger increment (manual trigger from clicking the
    // masthead — runs the same payoff without changing game state).
    // The status flip is itself deferred until `endReveal()` runs, so
    // by the time we observe playing → won here the per-cell flip
    // reveal has already settled — no extra delay needed.
    let prev = status.value;
    let prevTrigger = celebrationTrigger.value;
    const stopStatus = effect(() => {
      const trigger = celebrationTrigger.value;
      const curr = status.value;
      // Restoring a saved playthrough flips status playing → won inside
      // a batched write. Update the tracking vars so a subsequent real
      // win is still detected, but suppress the celebration itself —
      // we only want it on the live moment of victory.
      if (gameState.isHydrating) {
        prev = curr;
        prevTrigger = trigger;
        return;
      }
      if (trigger !== prevTrigger) {
        prevTrigger = trigger;
        fireWinCelebration();
      } else if (prev !== "won" && curr === "won") {
        fireWinCelebration();
      }
      prev = curr;
    });

    return () => {
      stopGreens();
      stopStatus();
      cancelPending();
    };
  }, []);

  return null;
}
